import React, { useCallback, useEffect, useRef, useState, Fragment } from 'react';
import { LoaderIcon, AlertCircleIcon } from 'lucide-react';
import { HeroSection } from '../components/HeroSection';
import { TrustSignals } from '../components/TrustSignals';
import { DegreeCategoryGrid } from '../components/DegreeCategoryGrid';
import { SEOContentSection } from '../components/SEOContentSection';
import { FAQSection } from '../components/FAQSection';
import { searchSchools, MatchedSchool } from '../services/matchingApi';
import { MatchedSchoolCard, DidYouKnowBanner } from '../components/MatchedSchoolCard';
import { HomeInlineLeadCapture } from '../components/HomeInlineLeadCapture';
import { ProgramContentSection } from '../components/ProgramContentSection';
import { useParams, useNavigate } from 'react-router-dom';
import { usePageMeta } from '../hooks/usePageMeta';
export function HomePage() {
  usePageMeta('OnlineCollegeGuide | Compare Accredited Online Degree Programs 2026', 'Compare accredited online colleges and universities. Get matched with degree programs that fit your career goals, schedule, and budget. 100% free service.', {
    ogImage: 'https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=1200&h=630&fit=crop&q=80',
    ogType: 'website'
  });
  const {
    areaStudy,
    program
  } = useParams<{
    areaStudy?: string;
    program?: string;
  }>();
  const navigate = useNavigate();
  const [matchedSchools, setMatchedSchools] = useState<MatchedSchool[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  // Track selected values for the content section
  const [selectedDegree, setSelectedDegree] = useState<string>('');
  const [selectedArea, setSelectedArea] = useState<string>('');
  const [selectedProgram, setSelectedProgram] = useState<string>('');
  // On narrow screens the hero and its widget fill the viewport, so results
  // rendered below it are invisible without scrolling — the visitor submits and
  // appears to get nothing back. Bring the matches into view once they arrive.
  const resultsRef = useRef<HTMLDivElement>(null);
  const pendingScrollRef = useRef(false);
  const fetchResults = useCallback(async (degreeInterest: string, areaStudyStr: string, mProgram: string) => {
    // Only search if mProgram (specialization) is selected
    if (!mProgram) {
      setHasSearched(false);
      return;
    }
    setSelectedDegree(degreeInterest);
    setSelectedArea(areaStudyStr);
    setSelectedProgram(mProgram);
    // Update URL if not already matching
    if (areaStudyStr && mProgram && (areaStudy !== areaStudyStr || program !== mProgram)) {
      navigate(`/degree/${areaStudyStr}/${mProgram}`, {
        replace: true
      });
    }
    pendingScrollRef.current = true;
    setIsLoading(true);
    setApiError(null);
    setHasSearched(true);
    const result = await searchSchools({
      degreeInterest: degreeInterest || undefined,
      areaStudy: areaStudyStr || undefined,
      mProgram: mProgram || undefined
    });
    if (result.success) {
      setMatchedSchools(result.schools);
    } else {
      setApiError('Unable to load school matches. Please try again.');
      setMatchedSchools([]);
    }
    setIsLoading(false);
  }, [navigate, areaStudy, program]);
  // On mount, if params exist, search
  useEffect(() => {
    if (areaStudy && program) {
      fetchResults('', areaStudy, program);
    }
  }, [areaStudy, program, fetchResults]);
  // Mobile only — on desktop the results already sit beside the fold.
  useEffect(() => {
    if (isLoading || !hasSearched || !pendingScrollRef.current) return;
    const target = resultsRef.current;
    if (!target || window.innerWidth >= 1024) {
      pendingScrollRef.current = false;
      return;
    }
    pendingScrollRef.current = false;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const raf = requestAnimationFrame(() => {
      target.scrollIntoView({
        behavior: reduceMotion ? 'auto' : 'smooth',
        block: 'start'
      });
    });
    return () => cancelAnimationFrame(raf);
  }, [isLoading, hasSearched]);
  return <main className="flex-grow bg-background" data-id="element-2006" data-mp="35ifjtrg52f62">
      <HeroSection onSelectionChange={fetchResults} data-id="element-2007" data-mp="30iksjmsgbnkh" />
      <TrustSignals data-id="element-2008" data-mp="30jvxvr1jp3dp" />

      {hasSearched ? <div ref={resultsRef} id="school-matches" className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-14 scroll-mt-20" data-id="element-2009" data-mp="30gq5a1tpjjd8">
          <div className="flex flex-col lg:flex-row gap-8" data-id="element-2010" data-mp="3t6qcw8flfajs">
            {/* Left Column: Results */}
            <div className="lg:w-2/3" data-id="element-2011" data-mp="3o7m3phso5ctg">
              {/* Loading State */}
              {isLoading && <div className="flex flex-col items-center justify-center py-16 bg-surface rounded-2xl border border-hairline shadow-ds" data-id="element-2012" data-mp="3k6ttal8291vl">
                  <LoaderIcon className="w-8 h-8 text-primary animate-spin mb-4" data-id="element-2013" data-mp="2ctrpz7hbqhgw" />
                  <p className="text-ink-secondary font-medium" data-id="element-2014" data-mp="28r54dob8xss5">
                    Finding your best school matches...
                  </p>
                </div>}

              {/* Error State */}
              {!isLoading && apiError && <div className="bg-red-50 border border-red-200 rounded-2xl p-6 flex items-start gap-3" data-id="element-2015" data-mp="sbh7lh3nkvg9">
                  <AlertCircleIcon className="w-5 h-5 text-error flex-shrink-0 mt-0.5" data-id="element-2016" data-mp="x8psvqk3lflq" />
                  <div data-id="element-2017" data-mp="122bvsr0gm54c">
                    <p className="text-ink font-semibold" data-id="element-2018" data-mp="122u1sfcxbxpg">{apiError}</p>
                    <p className="text-ink-secondary text-sm mt-1" data-id="element-2019" data-mp="16zc2n454vbwi">
                      Try adjusting your search criteria or check back later.
                    </p>
                  </div>
                </div>}

              {/* Results */}
              {!isLoading && !apiError && matchedSchools.length > 0 && <div className="space-y-4" data-id="element-2020" data-mp="1kz3ccsoma64p">
                  {matchedSchools.map((school, idx) => <Fragment key={(school as Record<string, unknown>).schoolid as string || idx} data-id="element-2021" data-mp="3hsyek935uv17" data-mpi={idx}>
                      <MatchedSchoolCard school={school} index={idx} data-id="element-2022" data-mp="3ms192zliliux" />
                      {idx === 0 && matchedSchools.length > 1 && <DidYouKnowBanner data-id="element-2023" data-mp="tx1e9ggn8a1m" />}
                    </Fragment>)}
                </div>}

              {/* Empty State */}
              {!isLoading && !apiError && matchedSchools.length === 0 && <div className="bg-surface rounded-2xl border border-hairline shadow-ds p-12 text-center" data-id="element-2024" data-mp="12y92t6tcxwii">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-background-alt mb-4" data-id="element-2025" data-mp="xxbgci1begb7">
                    <AlertCircleIcon className="w-8 h-8 text-primary" data-id="element-2026" data-mp="1ct9bpy3ezgmd" />
                  </div>
                  <h3 className="text-xl font-bold text-ink mb-2" data-id="element-2027" data-mp="17w0txym6heln">
                    No matches found
                  </h3>
                  <p className="text-ink-secondary max-w-md mx-auto" data-id="element-2028" data-mp="1hm2dcd3l68u8">
                    Try adjusting your degree level or program selection to find
                    more results.
                  </p>
                </div>}
            </div>

            {/* Right Column: Inline Lead Capture */}
            <div className="lg:w-1/3" data-id="element-2029" data-mp="2a8ka6t3ve0hk">
              <HomeInlineLeadCapture data-id="element-2030" data-mp="2a9lpj9rx5953" />
            </div>
          </div>

          {/* Dynamic Program Content Section */}
          {!isLoading && !apiError && selectedProgram && <ProgramContentSection degreeInterest={selectedDegree} areaOfStudy={selectedArea} specialization={selectedProgram} data-id="element-2031" data-mp="20ammq7tu47q0" />}
        </div> : <>
          <SEOContentSection data-id="element-2032" data-mp="257wbrmdbahdb" />
          <DegreeCategoryGrid data-id="element-2033" data-mp="25babemulcch2" />
          <FAQSection data-id="element-2034" data-mp="1rhsul9tahduq" />
        </>}
    </main>;
}